import { Injectable } from '@angular/core';
import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, Subscription, throwError, timer } from 'rxjs';
import { LoaderService } from '@core/services/loader/loader.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ErrorModalComponent } from '@shared/modals/error-modal/error-modal.component';
import { catchError, mergeMap, retryWhen } from 'rxjs/operators';
import { ShareService } from '@core/services/share-data/share-data.service';
import { NetworkStatus } from '@root/app.interfaces';
import { ATTEMPTS_DELAY_MS, MAX_RETRY_ATTEMPTS } from '@root/app.config';
import { ModalService } from '@core/services/modal/modal.service';
import { PaymentSessionServiceService } from '@core/services/payment-session-service/payment-session-service.service';

export const DISABLE_GLOBAL_EXCEPTION_HANDLING = new HttpContextToken<boolean>(() => false);
export const SUPPRESS_ERROR_MODAL = new HttpContextToken<boolean>(() => false);

@Injectable()
export class LoaderInterceptor implements HttpInterceptor {
	private requests: HttpRequest<any>[] = [];
	private modalStatus = true;

	networkStatus: NetworkStatus = {
		event: null,
		status: true,
	};
	getNetworkStatus: Subscription;

	constructor(
		private loaderService: LoaderService,
		private shareService: ShareService,
		private modalService: ModalService,
		private paymentSessionService: PaymentSessionServiceService,
		public dialog: MatDialog,
	) {
		this.getNetworkStatus = this.shareService.getNetworkStatus.subscribe((res: NetworkStatus) => {
			this.networkStatus = res;

			if (res.status) {
				this.modalStatus = true;
			}
		});
	}

	private removeRequest(req: HttpRequest<any>): void {
		const i = this.requests.indexOf(req);
		if (i >= 0) {
			this.requests.splice(i, 1);
		}

		this.getNetworkStatus.unsubscribe();
	}

	private isServerError(error: HttpErrorResponse): boolean {
		return error.status >= 0 && error.status < 404; // What? why would a server error be less than 404?
	}

	private errorModalHandler(error: Error) {
		this.modalService.openResponsiveDialog<{
			error: Error;
			network: NetworkStatus;
		}>({
			id: 'error-modal',
			component: ErrorModalComponent,
			data: { error, network: this.networkStatus },
			disableClose: true,
			maxWidth: '80%',
			maxHeight: '100vh',
			panelClass: '',
		});
	}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		this.requests.push(req);

		if (this.ignoreErrorHandling(req)) {
			return next.handle(req);
		}

		return next.handle(req).pipe(
			retryWhen((errors) =>
				errors.pipe(
					mergeMap((error, attempt) => {
						if (attempt < MAX_RETRY_ATTEMPTS && this.isServerError(error)) {
							return timer(ATTEMPTS_DELAY_MS);
						}
						return throwError(error);
					}),
				),
			),
			catchError((error: HttpErrorResponse) => {
				if (this.isOrderPaidError(error)) {
					this.handlePaidSession(req);
					return throwError(error);
				}

				if (!this.ignoreErrorModal(req) || !this.networkStatus.status) {
					if (this.networkStatus.event?.type === 'offline') {
						if (this.modalStatus) {
							this.errorModalHandler(error);

							this.modalStatus = false;
						}
					} else {
						this.errorModalHandler(error);
					}
				}

				this.removeRequest(req);
				return throwError(error);
			}),
		);
	}

	private ignoreErrorHandling<T>(request: HttpRequest<T>) {
		return request.context.get(DISABLE_GLOBAL_EXCEPTION_HANDLING);
	}

	private ignoreErrorModal<T>(request: HttpRequest<T>) {
		return request.context.get(SUPPRESS_ERROR_MODAL);
	}

	private isOrderPaidError(error: HttpErrorResponse): boolean {
		return error.status === 409 && error.error.code === 'ORDER_ALREADY_PAID';
	}

	private handlePaidSession<T>(request: HttpRequest<T>) {
		this.paymentSessionService.markAsPaid();
		this.paymentSessionService.redirectToHomePage();
		this.paymentSessionService.openOrderPaidModal();
		this.removeRequest(request);
	}
}
